// Pure helpers for the Camera Setting bulk operation, kept here so they can be unit tested.
import { RUN_TYPE_CAMERA_SETTING } from "./constants";

export const isPepperAdmin = (roles) => !!roles && roles.includes("PepperAdmin");

// Upload is allowed only for PepperAdmin, the Geeni brand, and a chosen setting and value.
export const canSubmitCameraSetting = ({ brand, isAdmin, setting, value }) =>
  brand === "geeni" && !!isAdmin && !!setting && value !== null && value !== undefined;

// Unique non-empty device ids after the "device id" header, matching how the backend counts.
export const countCsvDevices = (text) => {
  const lines = String(text || "").replace(/^﻿/, "").split(/\r?\n/).slice(1);
  return new Set(lines.map((line) => line.trim()).filter(Boolean)).size;
};

// Body for POST /bulk/update. Only camera-setting runs carry a setting and the resend flag.
export const buildProcessBody = ({ bucketName, runType, fileName, brand, setting, resendUnchanged }) => ({
  bucketName,
  runType,
  filePath: `public/${fileName}`,
  brand,
  ...(runType === RUN_TYPE_CAMERA_SETTING && setting
    ? { setting, resendUnchanged: !!resendUnchanged }
    : {}),
});

// Prefer the backend's reason ({ statusCode, error } on HTTP errors such as a 403) over the
// generic client message.
export const errorMessage = (err) => {
  const data = err?.response?.data;
  if (data && typeof data === "object" && (data.error || data.message)) return data.error || data.message;
  if (typeof data === "string" && data.trim()) {
    try {
      const parsed = JSON.parse(data);
      if (parsed.error || parsed.message) return parsed.error || parsed.message;
    } catch (e) {
      return data;
    }
  }
  return err?.message || "Request failed";
};

// How a run's background queueing state is shown in the run list. null means nothing to flag.
export const queueStatusLabel = ({ queueStatus, queueFailedCount, queueError }) => {
  switch (queueStatus) {
    case undefined:
    case null:
    case "queued":
      return null;
    case "queueing":
      return { text: "Queueing devices…", warning: false };
    case "queue_incomplete":
      return { text: `Queue incomplete: ${queueFailedCount} not queued`, warning: true };
    case "queue_failed":
      return { text: `Queue failed${queueError ? `: ${queueError}` : ""}`, warning: true };
    default:
      return { text: `Queue status: ${queueStatus}`, warning: true };
  }
};

// File Details receives at most a capped number of rows per kind for camera-setting runs.
// Returns a note when the run has more rows than are shown, otherwise null.
export const truncationNotice = (rows, totals) => {
  if (!totals || !Array.isArray(rows)) return null;
  const shownExceptions = rows.filter((row) => row.status === "exception").length;
  const shownCompleted = rows.length - shownExceptions;
  const parts = [];
  if (totals.exceptions > shownExceptions) parts.push(`${shownExceptions} of ${totals.exceptions} exceptions`);
  if (totals.processed > shownCompleted) parts.push(`${shownCompleted} of ${totals.processed} completed devices`);
  return parts.length ? `Showing ${parts.join(" and ")}. Counts on Bulk Updates cover the whole run.` : null;
};
